import { useCallback, useRef, useState } from 'react';
import { ComparisonSection } from '../api/comparisonApi';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ComparisonStage =
  | 'idle'
  | 'fetching'
  | 'extracting'
  | 'comparing'
  | 'summarizing'
  | 'done'
  | 'error';

export type CategoryStatus = 'pending' | 'comparing' | 'done';

export interface CategoryPill {
  category_id: string;
  category: string;
  is_standard: boolean;
  status: CategoryStatus;
}

export interface ComparisonSummary {
  executive_summary: string;
  overall_alignment: string;
  overall_similarity_score: number;
  unique_to_regulation_1: string[];
  unique_to_regulation_2: string[];
  common_grounds: string[];
  generated_at: string;
}

export interface RegulationMetaWS {
  artifact_id: string;
  title?: string | null;
  jurisdiction?: string | null;
  status?: string | null;
  effective_date?: string | null;
  // When this regulation's content was last fetched into our DB (data recency).
  fetched_at?: string | null;
}

export interface ComparisonSocketState {
  stage: ComparisonStage;
  stageMessage: string;
  totalCategories: number;
  categories: CategoryPill[];
  sections: ComparisonSection[];
  meta: { regulation_1: RegulationMetaWS; regulation_2: RegulationMetaWS } | null;
  summary: ComparisonSummary | null;
  error: string | null;
}

const INITIAL_STATE: ComparisonSocketState = {
  stage: 'idle',
  stageMessage: '',
  totalCategories: 0,
  categories: [],
  sections: [],
  meta: null,
  summary: null,
  error: null,
};

// Derive WS base URL from the API base URL
const API_BASE = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useComparisonSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ComparisonSocketState>(INITIAL_STATE);

  const reset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const compare = useCallback((artifactIds: [string, string], token: string) => {
    // Close any existing connection
    wsRef.current?.close();
    setState(INITIAL_STATE);

    const ws = new WebSocket(`${WS_BASE}/api/v1/comparison/ws?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ artifact_ids: artifactIds }));
    };

    ws.onmessage = (event: MessageEvent) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      setState(prev => {
        switch (msg.type as string) {
          case 'stage':
            return {
              ...prev,
              stage: msg.stage as ComparisonStage,
              stageMessage: msg.message ?? '',
              ...(msg.total_categories != null ? { totalCategories: msg.total_categories } : {}),
            };

          case 'meta':
            return { ...prev, meta: { regulation_1: msg.regulation_1, regulation_2: msg.regulation_2 } };

          case 'categories_list': {
            const categories: CategoryPill[] = (msg.categories ?? []).map((c: any) => ({
              category_id: c.category_id,
              category: c.category,
              is_standard: c.is_standard,
              status: 'pending' as CategoryStatus,
            }));
            return { ...prev, categories, totalCategories: categories.length };
          }

          case 'category_start': {
            const categories = prev.categories.map(c =>
              c.category_id === msg.category_id
                ? { ...c, status: 'comparing' as CategoryStatus }
                : c,
            );
            return { ...prev, categories };
          }

          case 'category_done': {
            const categories = prev.categories.map(c =>
              c.category_id === msg.category_id
                ? { ...c, status: 'done' as CategoryStatus }
                : c,
            );
            return { ...prev, categories, sections: [...prev.sections, msg.section as ComparisonSection] };
          }

          case 'summary':
            return { ...prev, summary: msg as ComparisonSummary };

          case 'done':
            return { ...prev, stage: 'done' };

          case 'error':
            return { ...prev, stage: 'error', error: msg.message ?? 'Unknown error' };

          default:
            return prev;
        }
      });
    };

    ws.onerror = () => {
      setState(prev => ({ ...prev, stage: 'error', error: 'WebSocket connection failed. Is the backend running?' }));
    };

    ws.onclose = () => {
      wsRef.current = null;
    };
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { state, compare, disconnect, reset };
}
